import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import { BaseResponse } from 'src/app/models/common/baseResponse';
import { PageResponse } from 'src/app/models/common/pageResponse';
import { IDictionaryKeyEntryDTO, DictionaryKeyEntryDTO } from 'src/app/models/languages-management/dictionary-key-entry-dto';
import { PaginationRequest } from 'src/app/models/common/paginationRequest';
import { DictionaryTranslatedDTO } from 'src/app/models/languages-management/dictionaryTranslatedDTO';
import { AppSessionService } from '../app-session/app-session.service';
import { setAdvancedFilterQuery, setSimpleFilterQuery } from '../../utils/tableUtils';
import { FilterOperation } from 'src/app/models/custom-table/custom-table.model';
import { GridUtils } from '../../utils/gridUtils';

const EMPTY_STRING = "";

@Injectable({ providedIn: 'root' })
export class DictionariesService {
    private BASE_URL = this.appSession.baseUrl + '/api/v1/dictionaries';

    constructor(
        private httpClient: HttpClient,
        private appSession: AppSessionService
    ) { }

    getLanguages(pageReq: PaginationRequest): Observable<BaseResponse<PageResponse<IDictionaryKeyEntryDTO>>> {
        const headers = new HttpHeaders({ 'Content-type': 'application/x-www-form-urlencoded; charset=utf-8' });
        let params = new HttpParams();

        if (pageReq) {
            params = pageReq.offset ? params.append('offset', pageReq.offset.toString()) : null;
            params = pageReq.realSize ? params.append('realSize', pageReq.realSize.toString()) : null;
            params = pageReq.limit ? params.append('limit', pageReq.limit.toString()) : null;
            params = pageReq.query ? params.append('query', pageReq.query) : null;
            params = pageReq.sort ? params.append('sort', pageReq.sort) : null;
        }
        const url = `${this.appSession.baseUrl + '/api/v1/dictionaries'}/management`;
        return this.httpClient.get<BaseResponse<PageResponse<IDictionaryKeyEntryDTO>>>(url, { headers, params });
    }

    addKey(key: DictionaryKeyEntryDTO): Observable<BaseResponse<IDictionaryKeyEntryDTO>> {
        const headers = new HttpHeaders({ 'Content-type': 'application/json' });
        const url = `${this.appSession.baseUrl + '/api/v1/dictionaries'}/management`;
        const data = JSON.stringify(key);
        return this.httpClient.post<BaseResponse<IDictionaryKeyEntryDTO>>(url, data, { headers });
    }

    editKey(origKey: DictionaryKeyEntryDTO, key: DictionaryKeyEntryDTO): Observable<BaseResponse<IDictionaryKeyEntryDTO>> {
        const headers = new HttpHeaders({ 'Content-type': 'application/json' });
        let params = new HttpParams().set('namespace', origKey.namespace).set('dictionaryKey', origKey.dictionaryKey);

        const url = `${this.appSession.baseUrl + '/api/v1/dictionaries'}/management`;

        const data = JSON.stringify(key);
        return this.httpClient.put<BaseResponse<IDictionaryKeyEntryDTO>>(url, data, { headers, params });
    }

    deleteKey(key: DictionaryKeyEntryDTO): Observable<BaseResponse<void>> {
        const headers = new HttpHeaders({ 'Content-type': 'application/json' });
        const params = new HttpParams()
            .set('namespace', key.namespace)
            .set('dictionaryKey', key.dictionaryKey);

        const url = `${this.appSession.baseUrl + '/api/v1/dictionaries'}/management`;

        return this.httpClient.delete<BaseResponse<void>>(url, { headers, params });
    }

    getNamespacesDictionary(namespace: string[], lang: string): Observable<BaseResponse<PageResponse<DictionaryTranslatedDTO>>> {
        const headers = new HttpHeaders().set('Content-type', 'application/json').set('locale', (lang + '-' + lang));
        let params = new HttpParams();
        const limit = 9999;

        namespace.forEach(name => {
            params = params.append('namespace', name);
        });
        const url = `${this.appSession.baseUrl + '/api/v1/dictionaries?limit=' + limit}`;
        return this.httpClient.get<BaseResponse<PageResponse<DictionaryTranslatedDTO>>>(url, { headers, params });
    }

    // get Dictionary List
    getDictionaryList(
      paging?: any,
      filteringArgs?: any,
      sortingArgs?: any,
      advancedFilteringArgs?: any
    ): Observable<any> {
      let headers = new HttpHeaders({
        "Content-type": "application/json",
      });

      let filterQuery = EMPTY_STRING;
      let advancedFilterQuery = EMPTY_STRING;
      let orderQuery = EMPTY_STRING;

      if (filteringArgs) {
        filterQuery = setSimpleFilterQuery(filteringArgs);
      }
  
      if (advancedFilteringArgs) {
        advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
      }
  
      if (sortingArgs) {
        const { active, direction } = sortingArgs;
        orderQuery += active + "," + direction.toUpperCase();
      }
  
      if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
        headers = headers.append(
          "query",
          `${filterQuery} ${
            filterQuery.length > 0 && advancedFilterQuery.length > 0
              ? FilterOperation.AND
              : ""
          } ${advancedFilterQuery}`
        );
      }
  
      if (orderQuery.length > 0) {
        headers = headers.append("sort", orderQuery);
      }
  
      return this.httpClient.get(
        this.buildDataUrl(
          paging,
          filteringArgs,
          sortingArgs,
          headers,
          "/managementAsProperties?"
        ),
        { headers }
      );
    }

    // get Dictionary
    getDictionary(action): Observable<any> {
      const headers = new HttpHeaders({
        'Content-type': 'application/json'
      });
      const url = `${this.appSession.baseUrl}/api/v1/dictionaries/managementAsProperties/${action.id}`;

      return this.httpClient.get(url, { headers });
    }

     // create dictionary
    createDictionary(action): Observable<any> {
      const headers = new HttpHeaders({
        'Content-type': 'application/json'
      });
      const data = JSON.stringify(action.dictionary);
      const url = `${this.appSession.baseUrl}/api/v1/dictionaries/management`;

      return this.httpClient.post(url, data, { headers });
    }

    // update dictionary
    updateDictionary(action): Observable<any> {
      const headers = new HttpHeaders({
        'Content-type': 'application/json'
      });
      const data = JSON.stringify(action.dictionary);
      const url = `${this.appSession.baseUrl}/api/v1/dictionaries/management`;

      return this.httpClient.put(url, data, { headers });
    }

    // delete dictionary
    deleteDictionary(action): Observable<any> {
      const headers = new HttpHeaders({
        'Content-type': 'application/json'
      });
      const url = `${this.appSession.baseUrl}/api/v1/dictionaries/management/${action.id}`;

      return this.httpClient.delete(url, { headers });
    }

    
  private buildDataUrl(
    pagingArgs: any,
    filteringArgs: any,
    sortingArgs: any,
    header: HttpHeaders,
    path: string
  ): string {
    let baseQueryString = `${this.appSession.baseUrl + '/api/v1/dictionaries'}${path}`; 
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = GridUtils._buildScrollExpression(pagingArgs);
    }

    query +=
      scrollingQuery !== EMPTY_STRING ? `&${scrollingQuery}` : EMPTY_STRING;

    baseQueryString += query;

    return baseQueryString;
  }
}